/**
 * Shared head() builders for the city and city+service landing routes.
 *
 * These routes' head() runs as critical (non-code-split) route code, so the
 * previously inlined per-file copies of this metadata/JSON-LD shipped ~36x in
 * the bundle every page downloads. Output is byte-identical to the inline
 * versions — this only de-duplicates the code that produces it.
 */

const SITE = "https://capitalpropressurewashing.com";

import { nearMeFaqs, serviceNearMeFaqs } from "../data/near-me-faq";

type Faq = { q: string; a: string };
type Meta = { title: string; description: string };

type CityServiceHeadData = {
  meta: Meta;
  heroImage: string;
  service: string;
  city: string;
  faqs: Faq[];
};

type CityHeadData = {
  meta: Meta;
  heroImage: string;
  city: string;
  faqs: Faq[];
};

const FALLBACK = { meta: [{ title: "Capital Pro Pressure Washing" }] };

function absImage(heroImage: string) {
  return heroImage.startsWith("http") ? heroImage : `${SITE}${heroImage}`;
}

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "5A Vatrano Rd",
  addressLocality: "Albany",
  addressRegion: "NY",
  postalCode: "12205",
  addressCountry: "US",
};

function faqJsonLd(faqs: Faq[]) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}

function socialMeta(m: Meta, url: string, image: string, twitterCard: boolean) {
  return [
    { title: m.title },
    { name: "description", content: m.description },
    { property: "og:title", content: m.title },
    { property: "og:description", content: m.description },
    { property: "og:url", content: url },
    { property: "og:type", content: "website" },
    { property: "og:image", content: image },
    ...(twitterCard ? [{ name: "twitter:card", content: "summary_large_image" }] : []),
    { name: "twitter:title", content: m.title },
    { name: "twitter:description", content: m.description },
    { name: "twitter:image", content: image },
  ];
}

export function cityServiceHead(
  slug: string,
  parentServiceSlug: string,
  c: CityServiceHeadData | undefined,
) {
  if (!c) return FALLBACK;
  const url = `${SITE}/${slug}`;
  const image = absImage(c.heroImage);
  return {
    meta: socialMeta(c.meta, url, image, false),
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${c.service} in ${c.city}, NY`,
          serviceType: c.service,
          provider: { "@id": `${SITE}/#business` },
          areaServed: {
            "@type": "City",
            name: c.city,
            containedInPlace: { "@type": "AdministrativeArea", name: "New York" },
          },
          url,
          description: c.meta.description,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            {
              "@type": "ListItem",
              position: 2,
              name: c.service,
              item: `${SITE}/${parentServiceSlug}`,
            },
            { "@type": "ListItem", position: 3, name: `${c.city}, NY`, item: url },
          ],
        }),
      },
      faqJsonLd([...c.faqs, ...serviceNearMeFaqs(c.service, c.city)]),
    ],
  };
}

export function cityHead(slug: string, c: CityHeadData | undefined) {
  if (!c) return FALLBACK;
  const url = `${SITE}/${slug}`;
  const image = absImage(c.heroImage);
  return {
    meta: socialMeta(c.meta, url, image, true),
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `Capital Pro Pressure Washing — ${c.city}, NY`,
          url,
          image,
          telephone: "+15189001913",
          priceRange: "$$",
          address: POSTAL_ADDRESS,
          parentOrganization: { "@id": `${SITE}/#business` },
          areaServed: {
            "@type": "City",
            name: c.city,
            containedInPlace: { "@type": "AdministrativeArea", name: "New York" },
          },
          description: c.meta.description,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: `${c.city}, NY`, item: url },
          ],
        }),
      },
      faqJsonLd([...c.faqs, ...nearMeFaqs(c.city)]),
    ],
  };
}
